import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Upload, X } from "lucide-react";
import swal from "sweetalert";
import {
  createBanner,
  getBannerDetail,
  updateBanner,
} from "../../../redux/banners/action";
import { setBannerDetail } from "../../../reusableSlice";
import { uploadImageToS3 } from "../../../utils/s3Upload";
import {
  PLACEMENT_OPTIONS,
  SIZE_OPTIONS,
  SURFACE_OPTIONS,
  PLACEMENT_VALUES,
  SIZE_VALUES,
  SURFACE_VALUES,
  S3_BANNERS_FOLDER,
  ALLOWED_IMAGE_TYPES,
  ALLOWED_IMAGE_ACCEPT,
} from "./constants";
import "../DynamicSections/HomePageCRM.css";
import "./Banners.css";

const EMPTY_FORM = {
  name: "",
  placement: "top",
  image: "",
  ar_image: "",
  link_url: "",
  ar_link_url: "",
  size: "half",
  surface: "both",
  is_active: true,
  starts_at: "",
  ends_at: "",
};

const toDateTimeLocal = (iso) => (iso ? String(iso).slice(0, 16) : "");

const buildPayload = (form) => {
  const payload = {
    placement: form.placement,
    size: form.size,
    surface: form.surface,
    // position deliberately omitted: on create the backend appends to the end
    // of the zone, and on edit it leaves the existing order alone. Order is
    // set by dragging on the Arrange tab.
    is_active: form.is_active,
    // Null rather than "" when blank: the column is nullable and an empty
    // string would be a name in reports that reads as a blank row.
    name: form.name.trim() || null,
    link_url: form.link_url.trim() || null,
    ar_link_url: form.ar_link_url.trim() || null,
    starts_at: form.starts_at || null,
    ends_at: form.ends_at || null,
  };
  if (form.image) payload.image = form.image;
  if (form.ar_image) payload.ar_image = form.ar_image;
  return payload;
};

const validate = (form) => {
  const errors = [];
  if (!PLACEMENT_VALUES.includes(form.placement)) errors.push("Placement is required");
  if (!SIZE_VALUES.includes(form.size)) errors.push("Size is invalid");
  if (!SURFACE_VALUES.includes(form.surface)) errors.push("Surface is invalid");
  if (!form.image && !form.ar_image) {
    errors.push("Upload at least one image (English or Arabic)");
  }
  if (form.starts_at && form.ends_at && new Date(form.starts_at) > new Date(form.ends_at)) {
    errors.push("End date must be after start date");
  }
  return errors;
};

const ImageUploadSlot = ({ id, label, value, uploading, onChange, onClear }) => {
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    try {
      await onChange(file);
    } catch (err) {
      swal({ text: err.message || "Upload failed", icon: "error", timer: 1800 });
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="ds-form-group">
      <label>{label}</label>
      {value ? (
        <div className="bn-image-preview">
          <img src={value} alt={label} />
          <button
            type="button"
            className="bn-image-preview__remove"
            onClick={onClear}
            aria-label={`Remove ${label}`}
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        <label htmlFor={id} className="bn-upload-area">
          {uploading ? (
            <span>Uploading...</span>
          ) : (
            <>
              <Upload size={16} />
              <span>Click to upload</span>
            </>
          )}
        </label>
      )}
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={ALLOWED_IMAGE_ACCEPT}
        style={{ display: "none" }}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
};

const BannerForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const detail = useSelector((state) => state.reusableSlice?.bannerDetail);

  const [form, setForm] = useState(EMPTY_FORM);
  const [pageLoading, setPageLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);

  // Track which id has been hydrated. Re-hydrate on id change (e.g. edit/1 → edit/2
  // with the form already mounted), but never re-hydrate the same id twice — that
  // would clobber the user's in-progress edits.
  const lastHydratedId = useRef(null);

  useEffect(() => {
    if (!isEdit) return undefined;
    setPageLoading(lastHydratedId.current !== id);
    dispatch(getBannerDetail(id));
    return () => dispatch(setBannerDetail(null));
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    if (!isEdit || !detail) return;
    // Guard against stale detail still in redux from the previous id.
    if (String(detail.id) !== String(id)) return;
    if (lastHydratedId.current === id) return;
    lastHydratedId.current = id;
    setForm({
      name: detail.name || "",
      placement: detail.placement || "top",
      image: detail.image || "",
      ar_image: detail.ar_image || "",
      link_url: detail.link_url || "",
      ar_link_url: detail.ar_link_url || "",
      size: detail.size || "half",
      surface: detail.surface || "both",
      is_active: detail.is_active ?? true,
      starts_at: toDateTimeLocal(detail.starts_at),
      ends_at: toDateTimeLocal(detail.ends_at),
    });
    setPageLoading(false);
  }, [detail, isEdit, id]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const uploadImage = async (field, file) => {
    setUploadingField(field);
    try {
      const { file_url } = await uploadImageToS3(file, {
        folder: S3_BANNERS_FOLDER,
        allowedTypes: ALLOWED_IMAGE_TYPES,
      });
      setField(field, file_url);
    } finally {
      setUploadingField(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validate(form);
    if (errors.length > 0) {
      swal({ text: errors[0], icon: "warning", timer: 2000 });
      return;
    }
    const payload = buildPayload(form);
    const onSuccess = () => navigate("/banners");
    if (isEdit) {
      dispatch(updateBanner({ id: Number(id), ...payload }, setSubmitting, onSuccess));
    } else {
      dispatch(createBanner(payload, setSubmitting, onSuccess));
    }
  };

  const headerTitle = isEdit ? "Edit Banner" : "Create Banner";
  const headerHint = useMemo(
    () =>
      isEdit
        ? "Update placement, scheduling, and visuals for this banner."
        : "Add a new banner to the top hero, middle, or pre-footer zone.",
    [isEdit],
  );

  if (pageLoading) {
    return (
      <div className="ds-container">
        <div className="ds-empty-state" style={{ height: 200 }}>Loading banner...</div>
      </div>
    );
  }

  return (
    <div className="ds-container">
      <div className="ds-header">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            type="button"
            className="ds-btn-secondary"
            onClick={() => navigate("/banners")}
            style={{ padding: "6px 10px" }}
            aria-label="Back to banners"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <h4>{headerTitle}</h4>
            <p>{headerHint}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="ds-form-container">
          {/* ── Placement & visibility ── */}
          <div className="ds-form-section">
            <h5>Placement & Visibility</h5>
            <div className="ds-form-grid">
              <div className="ds-form-group">
                <label>Campaign name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder="e.g. Eid Fragrance Push"
                  maxLength={255}
                />
                <span className="bn-help-text">
                  Optional, and never shown to customers — this is what the
                  banner is called in analytics and in the banner list. Without
                  one it reports as its link URL. Keep it in English so one
                  campaign stays one row in reports.
                </span>
              </div>
              <div className="ds-form-group">
                <label>Placement *</label>
                <select
                  value={form.placement}
                  onChange={(e) => setField("placement", e.target.value)}
                >
                  {PLACEMENT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="ds-form-group">
                <label>Size</label>
                <select
                  value={form.size}
                  onChange={(e) => setField("size", e.target.value)}
                >
                  {SIZE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="ds-form-group">
                <label>Surface</label>
                <select
                  value={form.surface}
                  onChange={(e) => setField("surface", e.target.value)}
                >
                  {SURFACE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              {/* Position is set by dragging on the Arrange tab. Typed here it
                  could only be read against the section screen's numbers,
                  which this form never showed. */}
              <div className="ds-form-group">
                <label>Active</label>
                <div className="ds-toggle-row">
                  <label className="ds-toggle">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => setField("is_active", e.target.checked)}
                    />
                    <span className="ds-toggle-slider" />
                  </label>
                  <span style={{ fontSize: 13 }}>
                    {form.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Creative ── */}
          <div className="ds-form-section">
            <h5>Creative</h5>
            <p className="bn-help-text" style={{ marginTop: -8, marginBottom: 12 }}>
              Upload English and/or Arabic artwork. At least one is required.
            </p>
            <div className="ds-form-grid">
              <ImageUploadSlot
                id="image-en"
                label="Image (English)"
                value={form.image}
                uploading={uploadingField === "image"}
                onChange={(file) => uploadImage("image", file)}
                onClear={() => setField("image", "")}
              />
              <ImageUploadSlot
                id="image-ar"
                label="Image (Arabic)"
                value={form.ar_image}
                uploading={uploadingField === "ar_image"}
                onChange={(file) => uploadImage("ar_image", file)}
                onClear={() => setField("ar_image", "")}
              />
              <div className="ds-form-group">
                <label>Link URL (English)</label>
                <input
                  type="text"
                  value={form.link_url}
                  onChange={(e) => setField("link_url", e.target.value)}
                  placeholder="e.g. /brand/nars or https://..."
                />
                <span className="bn-help-text">
                  Optional. Leave blank to keep the banner non-clickable.
                </span>
              </div>
              <div className="ds-form-group">
                <label>Link URL (Arabic)</label>
                <input
                  type="text"
                  value={form.ar_link_url}
                  onChange={(e) => setField("ar_link_url", e.target.value)}
                  placeholder="e.g. /brand/nars or https://..."
                />
                <span className="bn-help-text">
                  Optional. Arabic visitors fall back to the English link when
                  this is blank.
                </span>
              </div>
            </div>
          </div>

          {/* ── Scheduling ── */}
          <div className="ds-form-section">
            <h5>Scheduling (optional)</h5>
            <div className="ds-form-grid">
              <div className="ds-form-group">
                <label>Start Date</label>
                <input
                  type="datetime-local"
                  value={form.starts_at}
                  onChange={(e) => setField("starts_at", e.target.value)}
                />
              </div>
              <div className="ds-form-group">
                <label>End Date</label>
                <input
                  type="datetime-local"
                  value={form.ends_at}
                  onChange={(e) => setField("ends_at", e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* ── Actions ── */}
          <div className="ds-form-actions">
            <button
              type="button"
              className="ds-btn-secondary"
              onClick={() => navigate("/banners")}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="ds-btn-primary"
              disabled={submitting || Boolean(uploadingField)}
            >
              {submitting ? "Saving..." : isEdit ? "Update Banner" : "Create Banner"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BannerForm;
